import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { IconButton, Link } from '@mui/material';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import UserContext from '../../../context/userContext';
import { errorToast, infoToast, successToast } from '../../Toast';
import { ClientsApi, RolesApi } from './api';
import ConfirmationModal from './components/ConfirmationModal';
import { Client } from './types';

const AdministrationClientDetails = () => {
	const { selectedUserRole: user } = React.useContext(UserContext);
	const queryClient = useQueryClient();
	let { clientUuid } = useParams();
	const navigate = useNavigate();

	const CLIENT_KEY = ClientsApi.CLIENT_KEY + '-' + user.namespace;
	const ROLES_KEY = RolesApi.ROLES_KEY + '-' + user.namespace;

	const [open, setOpen] = React.useState(false);
	const [secret, setSecret] = React.useState(
		queryClient.getQueryData<string>([
			CLIENT_KEY,
			clientUuid,
			'new-client-secret',
		]),
	);

	const { data: rolesData } = useQuery({
		queryKey: [ROLES_KEY],
		queryFn: RolesApi.getRoles,
	});

	const { isLoading: loadingClient, data: client } = useQuery({
		queryKey: [CLIENT_KEY, clientUuid],
		queryFn: () => ClientsApi.getClient(clientUuid),
		retry: false,
		onError: (error) => {
			if (axios.isAxiosError(error) && error.response?.status === 409) {
				errorToast('Client not found');
				navigate('/admin/clients', { replace: true });
			}
		},
	});

	const role = rolesData?.find((role) => role.role === client?.role);

	const clientSecretMutation = useMutation(ClientsApi.regenerateClientSecret, {
		onSuccess: (data) => {
			setSecret(data);
			successToast('Secret regenerated');
		},
		onError: () => {
			errorToast('Error regenerating secret');
		},
	});

	const deleteClientMutation = useMutation(ClientsApi.deleteClient, {
		onSuccess: () => {
			// Remove this client from cache
			queryClient.setQueryData([CLIENT_KEY, clientUuid], null);

			// Remove from list of clients
			const currClients = queryClient.getQueryData<Client[]>([CLIENT_KEY]);
			const newClients = currClients.filter((c) => c.clientUuid !== clientUuid);
			queryClient.setQueryData([CLIENT_KEY], newClients);

			// Refresh the clients list
			queryClient.invalidateQueries([CLIENT_KEY]);

			successToast('Successfully deleted client');

			setOpen(false);
			navigate('/admin/clients', { replace: true });
		},
		onError: (err) => {
			errorToast('Error deleting client');
		},
	});

	React.useEffect(() => {
		// Immediately remove this from the cache
		queryClient.setQueryData(
			[CLIENT_KEY, clientUuid, 'new-client-secret'],
			() => null,
		);
	}, [CLIENT_KEY, clientUuid, queryClient]);

	const handleOpenDeleteModal = () => {
		setOpen(true);
	};

	const handleCloseDeleteModal = () => {
		setOpen(false);
	};

	const handleRegenerateSecret = () => {
		clientSecretMutation.mutate(clientUuid);
	};

	const handleDeleteClient = () => {
		deleteClientMutation.mutate(clientUuid);
	};

	const copySecretToClipboardAndAlertUser = (secret: string) => {
		navigator.clipboard.writeText(secret);
		infoToast('Secret copied to clipboard');
	};

	return (
		<Container maxWidth='md'>
			<Paper sx={{ pl: 2, pr: 2, pt: 1, pb: 1 }}>
				<Grid
					container
					justifyContent='space-between'
					alignItems='center'
					sx={{ pl: 0, pr: 0, pt: 2, pb: 2 }}
				>
					<Grid item>
						{loadingClient ? (
							<Skeleton width={200} height={32} />
						) : (
							<Typography variant='h6'>Client Details</Typography>
						)}
					</Grid>
					<Grid item>
						{loadingClient ? (
							<Skeleton sx={{ minWidth: 0, height: 32, width: 32 }} />
						) : (
							<Tooltip title='Delete client' aria-label='delete client'>
								<IconButton
									disabled={deleteClientMutation.isLoading}
									onClick={handleOpenDeleteModal}
								>
									<DeleteForeverIcon color='inherit' />
								</IconButton>
							</Tooltip>
						)}
					</Grid>
				</Grid>
				<Grid container direction='row' justifyContent='space-between'>
					{loadingClient ? (
						<Grid item>
							<Skeleton width={200} height={32} />
						</Grid>
					) : (
						<>
							<Grid item xs={6} sx={{ pt: 2 }}>
								<Typography variant='caption'>Client ID</Typography>
								<Typography variant='body2'>{client?.clientId}</Typography>
							</Grid>
							{role?.namespace && (
								<Grid item xs={6} sx={{ pt: 2 }}>
									<Typography variant='caption'>Namespace</Typography>
									<Typography variant='body2'>{role.namespace}</Typography>
								</Grid>
							)}
							{role?.tenantHostname && (
								<Grid item xs={6} sx={{ pt: 2 }}>
									<Typography variant='caption'>Tenant</Typography>
									<Typography variant='body2'>
										<Link
											component={RouterLink}
											to={`/admin/tenants/${role.tenantHostname}/${
												role?.accountTypeDisplayName
													? 'integrations/script-logs'
													: 'users'
											}`}
										>
											{role.tenantHostname}
										</Link>
									</Typography>
								</Grid>
							)}
							{(role?.namespaceRoleTypeDisplayName ||
								role?.accountTypeDisplayName) && (
								<Grid item xs={6} sx={{ pt: 2 }}>
									<Typography variant='caption'>Role</Typography>
									<Typography variant='body2'>
										{role.namespaceRoleTypeDisplayName ||
											role.accountTypeDisplayName}
									</Typography>
								</Grid>
							)}
							<Grid item xs={6} sx={{ pt: 2 }}>
								<Typography variant='caption'>Secret</Typography>
								{clientSecretMutation.isLoading ? (
									<Skeleton width={200} height={34} />
								) : (
									<Typography variant='body2'>
										{secret || client?.clientSecret}
										{secret && !clientSecretMutation.isLoading && (
											<Tooltip title='Copy secret to clipboard'>
												<IconButton
													size='small'
													onClick={() =>
														copySecretToClipboardAndAlertUser(
															secret || client?.clientSecret,
														)
													}
												>
													<ContentCopyIcon />
												</IconButton>
											</Tooltip>
										)}
									</Typography>
								)}
							</Grid>
						</>
					)}
				</Grid>
				<Grid
					container
					justifyContent='space-between'
					alignItems='center'
					sx={{ pl: 0, pr: 0, pt: 4, pb: 2 }}
				>
					<Grid item>
						{loadingClient ? (
							<Skeleton width={200} height={32} />
						) : (
							<Button
								variant='outlined'
								disabled={deleteClientMutation.isLoading}
								component={RouterLink}
								to='/admin/clients'
							>
								{'Back'}
							</Button>
						)}
					</Grid>
					<Grid item>
						{loadingClient ? (
							<Skeleton width={200} height={32} />
						) : (
							<Button
								color='secondary'
								variant='contained'
								onClick={handleRegenerateSecret}
							>
								Regenerate Secret
							</Button>
						)}
					</Grid>
				</Grid>
			</Paper>
			<ConfirmationModal
				headerText='Are you sure you want to delete this client?'
				bodyText='This cannot be undone.'
				open={open}
				handleClose={handleCloseDeleteModal}
				handleSubmit={handleDeleteClient}
			/>
		</Container>
	);
};

export default AdministrationClientDetails;
